import React from "react";
import { EyeIcon } from "@heroicons/react/outline";
import { ButtonBase } from "@reusejs/react";
import { amountFormatter } from "services";
import { useRouter } from "next/router";
import { DateTime } from "luxon";
import { CheckCircle, Landmark } from "lucide-react";
import GreenIcon from "../Icons/GreenIcon";

export interface PropertyCardProps {
  id: number;
  price: number;
  views: number;
  interested: number;
  auction_date: string;
  emd_amount: number;
  bank_name: string;
  verified_by: string;
  location: {
    city: string;
    state: string;
  };
  emd_deposit_ends_at: number;
  cta: string;
  mostPopular: boolean;
  onClick: any;
  borderClasses: any;
  top: boolean;
  widthClasses: any;
  property: any;
  interestedCount: number;
}

let cardWrapper =
  "relative bg-white border border-gray-200 shadow-sm flex flex-col";

export default function FeaturedPropertyDetailCard(props: PropertyCardProps) {
  const router = useRouter();
  const handleClick = () => {
    const newTab: any = window.open(
      `/auction-properties/${props.property?.listing_id}`,
      `_blank`
    );
    newTab.focus();
    // router.redirect(`/auction-properties/${props.property?.listing_id}`);
    // props.onClick();
  };
  const current_date_object = DateTime.now().setZone("Asia/Kolkata").set({
    hour: 0,
    minute: 0,
    second: 0,
    millisecond: 0,
  });
  const emd_last_date_object = DateTime.fromISO(
    props.property.emd_last_date && props.property.emd_last_date
  ).setZone("Asia/Kolkata");
  const diffDays = Math.floor(
    emd_last_date_object.diff(current_date_object, "days").days
  );
  return (
    <div
      key={props.property?.uuid}
      className={`${cardWrapper} ${props.borderClasses} ${props.widthClasses}`}
    >
      <div className="flex-1">
        {!props.top && (
          <div className="text-left px-4 py-3 border-b text-xs text-gray-500">
            Assets ID: {props.property?.listing_id}
          </div>
        )}
        <div className="text-left border-b px-4 py-5 flex justify-between h-24">
          <div className="flex basis-1/2 flex-col">
            <div className="text-xs text-gray-500 capitalize">
              {props.property?.city?.name}
            </div>
            <div className="text-sm capitalize">
              {props.property?.state?.name}
            </div>
          </div>
          <div className="flex basis-1/2 flex-col justify-start items-end">
            {props.property.property_date_type === "dated" && (
              <div>
                {diffDays > 0 ? (
                  <>
                    <div className="text-xs text-red-500">
                      EMD Deposit ends in
                    </div>
                    <div className="text-sm font-bold text-red-500">
                      {diffDays} Days
                    </div>
                  </>
                ) : diffDays === 0 ? (
                  <>
                    <div className="text-xs text-red-500">EMD Deposit ends</div>
                    <div className="text-sm font-bold text-red-500">Today</div>
                  </>
                ) : (
                  <div className="text-sm text-red-500">
                    EMD deposit deadline has ended.
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="text-left border-b px-4 py-6 flex justify-between">
          <div className="flex basis-1/2 flex-col">
            <div className="text-sm text-gray-500">Reserve Price</div>
            <div className="text-xl font-bold text-blue-600">
              {props.property.property_date_type === "undated" ? (
                <div
                  className="cursor-pointer hover:text-blue-700"
                  onClick={() => {
                    const baseurl = window.location.href
                      .split("/")
                      .filter(Boolean)[1];
                    const link =
                      "https://" +
                      baseurl +
                      "/auction-properties/" +
                      props.property.listing_id;

                    window.open(
                      `https://wa.me/918370969696?text=${encodeURI(
                        `Hello, I would like to know the price of this property: ${link}`
                      )}`,
                      "_blank"
                    );
                  }}
                >
                  Contact Us
                </div>
              ) : (
                `₹${amountFormatter(props.property?.reserve_price)}`
              )}
            </div>
          </div>
        </div>
        <div className="text-left border-b px-4 flex justify-between">
          <div className="flex basis-1/2 border-r py-4 flex-col">
            <div className="text-xs text-gray-500">Auction Date</div>
            <div className="text-sm">
              {" "}
              {props.property.property_date_type === "undated" ? (
                <span className="text-red-600">Coming Soon</span>
              ) : props.property.property_date_type === "private_treaty" ? (
                <span className="text-green-600">Private Treaty</span>
              ) : (
                props.property?.auction_start_date_time &&
                DateTime.fromISO(props.property?.auction_start_date_time)
                  .setZone("Asia/Kolkata")
                  .toFormat("dd MMMM yyyy")
              )}
            </div>
          </div>
          <div className="flex basis-1/2 py-4 flex-col pl-6">
            <div className="text-xs text-gray-500">EMD Amount</div>
            <div className="text-sm">
              {props.property.property_date_type === "undated" ? (
                <span className="text-red-600">Coming Soon</span>
              ) : props.property.property_date_type === "private_treaty" ? (
                <span className="text-green-600">Private Treaty</span>
              ) : (
                `₹${amountFormatter(props.property?.emd_amount)}`
              )}
            </div>
          </div>
        </div>
        <div className="text-left border-b px-4 py-4 flex flex-col gap-y-1 h-16">
          {props?.property?.bank?.name && (
            <div className="text-xs flex items-center text-gray-500 capitalize">
              <Landmark className="w-4 h-4 mr-2 text-gray-600 flex-shrink-0" />
              <span className="ml-1">{props.property?.bank?.name}</span>
            </div>
          )}
          {props?.property?.ab_verified && (
            <div className="text-xs flex items-center text-blue-600 ">
              <CheckCircle className="w-4 h-4 mr-2 text-green-600 flex-shrink-0" />
              Verified by Auction Bazaar
            </div>
          )}
        </div>
        <div className="text-left border-b px-4 py-2 bg-gray-100 flex justify-between items-center">
          <div className="flex flex-col">
            <div className="text-xs text-gray-500">
              {`${
                props.interestedCount === 0
                  ? props.interestedCount
                  : props.interestedCount + "+"
              } People are interested`}
            </div>
          </div>
          <div className="flex flex-col">
            <div className="flex items-center text-xs text-blue-600">
              <EyeIcon width="20" height="20" className="mr-1" />
              {`${
                props.property.view_count === 0
                  ? props.property.view_count
                  : props.property.view_count + "+"
              } Views`}
            </div>
          </div>
        </div>
        <div className="p-4">
          <ButtonBase
            variant="primary"
            type="submit"
            label={"View Details"}
            onClick={() => handleClick()}
            buttonBaseClasses={{
              font: "text-sm font-medium",
              backgroundColor: "bg-blue-600 hover:bg-blue-700",
              textColor: "text-white",
              border: "any",
              width: "w-full",
              padding: "px-4 py-3",
            }}
          />
        </div>
      </div>
    </div>
  );
}